import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Layout, BackTop, Drawer, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useMediaQuery } from '@react-hook/media-query';
import Sidenav from './components/Sidenav';
import Header from './components/Header';
import LoginPage from './components/LoginPage';
import { AuthContext } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AirtimeTopup from './components/AirtimeTopup';
import AccountInfo from './components/AccountInfo';
import Transactions from './components/Transactions';
import TransferFund from './components/TransferFund';
import PayBills from './components/PayBills';
import Support from './components/Support';
import SettingsPage from './components/SettingsPage';
import RegistrationPage from './components/RegistrationPage';

const MemoizedAirtimeTopup = React.memo(AirtimeTopup);
const MemoizedAccountInfo = React.memo(AccountInfo);
const MemoizedTransactions = React.memo(Transactions);
const MemoizedTransferFund = React.memo(TransferFund);
const MemoizedPayBills = React.memo(PayBills);
const MemoizedSupport = React.memo(Support);

const { Content, Sider } = Layout;

function App() {
  const [isSidenavActive, setIsSidenavActive] = useState(false);
  const [render, setRender] = useState(false);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { isLoggedIn, login, logout, user, isLocked, setIsLocked } = useContext(AuthContext);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const appRef = useRef(null);

  useEffect(() => {
    const checkIsAppInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
        setIsAppInstalled(true);
      }
    };

    checkIsAppInstalled();

    window.addEventListener('appinstalled', () => {
      setIsAppInstalled(true);
      setInstallPromptEvent(null);
    });

    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      setInstallPromptEvent(event);
    });

    return () => {
      window.removeEventListener('appinstalled', () => {
        setIsAppInstalled(true);
        setInstallPromptEvent(null);
      });
      window.removeEventListener('beforeinstallprompt', (event) => {
        event.preventDefault();
        setInstallPromptEvent(event);
      });
    };
  }, []);

  const toggleSidenav = () => {
    setIsSidenavActive(!isSidenavActive);
  };

  const forceRender = () => {
    setRender(!render);
  };

  const contextValue = React.useMemo(() => ({
    user,
    isLoggedIn,
    login,
    logout,
    isLocked,
    setIsLocked,
  }), [user, isLoggedIn, login, logout, isLocked, setIsLocked]);

  return (
    <div ref={appRef}>
      {console.log("Re Render is here -------- ", render)}
      <AuthContext.Provider value={contextValue}>
        <Router>
          <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
            <Layout
              className="site-layout"
              style={{
                marginLeft: (isMobile || !isLoggedIn) ? 0 : 256,
                backgroundColor: '#f0f2f5',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Header toggleSidenav={toggleSidenav} style={{ position: 'sticky', top: 0, zIndex: 1 }} />
              <Content
                style={{
                  margin: '0',
                  padding: 0,
                  textAlign: 'center',
                  backgroundColor: '#f0f2f5'
                }}
              >
                <Routes>
                  <Route path="/" element={isLoggedIn ? <Navigate to="/airtime" /> : <LoginPage />} />
                  <Route path="/register" element={<RegistrationPage />} />
                  <Route path="/airtime" element={<ProtectedRoute><MemoizedAirtimeTopup /></ProtectedRoute>} />
                  <Route path="/account" element={<ProtectedRoute><MemoizedAccountInfo load = {true} /></ProtectedRoute>} />
                  <Route path="/transactions" element={<ProtectedRoute><MemoizedTransactions /></ProtectedRoute>} />
                  <Route path="/transfer" element={<ProtectedRoute><MemoizedTransferFund /></ProtectedRoute>} />
                  <Route path="/bills" element={<ProtectedRoute><MemoizedPayBills /></ProtectedRoute>} />
                  <Route path="/support" element={<ProtectedRoute><MemoizedSupport /></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                </Routes>
                {!isAppInstalled && installPromptEvent && (
                  <Button
                    onClick={() => {
                      installPromptEvent.prompt();
                      installPromptEvent.userChoice.then((choiceResult) => {
                        if (choiceResult.outcome === 'accepted') {
                          console.log('User accepted the install prompt');
                        } else {
                          console.log('User dismissed the install prompt');
                        }
                        setInstallPromptEvent(null);
                      });
                    }}
                    style={{
                      marginTop: '40px',
                      position: 'fixed',
                      // bottom: '20px',
                      // left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'white',
                      color: 'green',
                      border: '1px solid green',
                      outline: 'none',
                    }}
                  >
                    <DownloadOutlined style={{ border: '1px solid green' }} /> Install App
                  </Button>
                )}
              </Content>
            </Layout>
            {isLoggedIn ? (
              isMobile ? (
                <Drawer
                  placement="left"
                  closable={false}
                  onClose={toggleSidenav}
                  open={isSidenavActive}
                  width={256}
                  style={{ zIndex: 1000 }}
                  styles={{
                    body: {
                      margin: 0,
                      padding: 0,
                      height: '100vh',
                      left: 0,
                      top: 0,
                      bottom: 0,
                    }
                  }}
                >
                  <Sidenav toggleSidenav={toggleSidenav} isActive={isSidenavActive} />
                </Drawer>
              ) : (
                <Sider
                  trigger={null}
                  collapsible
                  collapsed={false}
                  width={256}
                  style={{
                    overflow: 'auto',
                    height: '100vh',
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    bottom: 0,
                  }}
                >
                  <div className="demo-logo-vertical" />
                  <Sidenav isActive={isSidenavActive} />
                </Sider>
              )
            ) : null}
            <BackTop />
          </Layout>
        </Router>
      </AuthContext.Provider>
    </div>
  );
}

export default App;
